import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Trash2, ArrowLeft, FolderOpen, Home, Upload as UploadIcon, Settings, BarChart3 } from 'lucide-react';
import ContentIcon from '../components/ContentIcon';
import LevelUpHeader from '../components/LevelUpHeader';
import SunsetBanner from '../components/SunsetBanner';
import LevelUpFooter from '../components/LevelUpFooter';

function MyContent({ user, onLogout }) {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyAssets();
  }, []);

  const fetchMyAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assets?mine=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      setAssets(data.assets || []);
    } catch (error) {
      toast.error('Failed to load your content');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (assetId, assetTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${assetTitle}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assets/${assetId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Asset deleted successfully');
        fetchMyAssets();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to delete asset');
      }
    } catch (error) {
      console.error('Delete asset error:', error);
      toast.error('Connection error. Please try again.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
      {/* LevelUp Header */}
      <LevelUpHeader user={user} onLogout={onLogout} />
      <SunsetBanner user={user} />

      <div style={{ flex: 1 }}>
        {/* Hero Section with Back to Home */}
        <div style={{ background: 'linear-gradient(135deg, #B9DCD2 0%, #E8F5E9 100%)', padding: '40px 24px' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <button 
              onClick={() => navigate('/browse')} 
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#1B5E5F', fontSize: '14px', fontWeight: '500', marginBottom: '16px' }}
            >
              <ArrowLeft size={18} />
              Back to Why Microsoft – Compete Enablement for Partners
            </button>
            <h1 style={{ fontSize: '36px', fontWeight: '600', color: '#2C3E50', marginBottom: '8px' }}>My Content</h1>
            <p style={{ fontSize: '16px', color: '#5A6C7D' }}>Manage your uploaded assets</p>
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: '1fr 280px', gap: '32px' }}>
          <main>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#2C3E50' }}>Your Assets ({assets.length})</h2>
            <button 
              onClick={() => navigate('/upload')}
              style={{ backgroundColor: '#0C6A1D', color: 'white', padding: '12px 24px', borderRadius: '4px', border: 'none', fontWeight: '600', cursor: 'pointer' }}
            >
              Upload New Asset
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>Loading your content...</div>
          ) : assets.length === 0 ? (
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '60px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <FolderOpen size={48} color="#B9DCD2" style={{ marginBottom: '16px' }} />
              <p style={{ fontSize: '18px', color: '#5A6C7D', marginBottom: '24px' }}>You haven't uploaded any content yet.</p>
              <button 
                onClick={() => navigate('/upload')}
                style={{ backgroundColor: '#0C6A1D', color: 'white', padding: '12px 24px', borderRadius: '4px', border: 'none', fontWeight: '600', cursor: 'pointer' }}
              >
                Upload Your First Asset
              </button>
            </div>
          ) : (
            <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#F5F5F5' }}>
                  <tr>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#5A6C7D' }}>Asset</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#5A6C7D' }}>Solution Area</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#5A6C7D' }}>Type</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#5A6C7D' }}>Size</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#5A6C7D' }}>Downloads</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#5A6C7D' }}>Click Rate</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#5A6C7D' }}>Version</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#5A6C7D' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map(asset => (
                    <tr key={asset.id} style={{ borderTop: '1px solid #E0E0E0' }}>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <ContentIcon type={asset.content_type} className="w-8 h-8" />
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#2C3E50' }}>{asset.title}</div>
                            <div style={{ fontSize: '12px', color: '#95A5A6' }}>{asset.solution_play || (asset.conversations && asset.conversations.join(', '))}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#5A6C7D' }}>{asset.solution_area}</td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#5A6C7D' }}>{asset.content_type}</td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#5A6C7D' }}>
                        {(asset.file_size / 1024 / 1024).toFixed(2)} MB
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#5A6C7D' }}>
                        {asset.content_type === 'Web URL' ? '—' : (asset.download_count || 0)}
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#5A6C7D' }}>
                        {asset.content_type === 'Web URL' ? (asset.download_count || 0) : '—'}
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#5A6C7D' }}>
                        {asset.content_build_date ? new Date(asset.content_build_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleDelete(asset.id, asset.title)}
                            style={{ padding: '6px 12px', backgroundColor: '#DC3E15', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>

        {/* Quick Links Sidebar */}
        <aside style={{ height: 'fit-content' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#2C3E50', marginBottom: '16px' }}>Quick Links</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={() => navigate('/browse')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F5F5F5', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#2C3E50', fontWeight: '500' }}>
                <Home size={18} color="#0C6A1D" />
                <span>Home</span>
              </button>
              <button onClick={() => navigate('/upload')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F5F5F5', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#2C3E50', fontWeight: '500' }}>
                <UploadIcon size={18} color="#0C6A1D" />
                <span>Upload Content</span>
              </button>
              {user.roles.includes('admin') && (
                <button onClick={() => navigate('/admin')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F5F5F5', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#2C3E50', fontWeight: '500' }}>
                  <Settings size={18} color="#0C6A1D" />
                  <span>Admin Panel</span>
                </button>
              )}
              {(user.roles.includes('admin') || user.roles.includes('report_viewer')) && (
                <button onClick={() => navigate('/analytics')} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', backgroundColor: '#F5F5F5', border: 'none', borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#2C3E50', fontWeight: '500' }}>
                  <BarChart3 size={18} color="#0C6A1D" />
                  <span>Analytics</span>
                </button>
              )}
            </div>
          </div>
        </aside>
      </div>
      </div>

      <LevelUpFooter />
    </div>
  );
}

export default MyContent;
